import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import clsx from "clsx";
import { Check, Clock, PenLine, X } from "lucide-react";
import {
  PurchaseDocDetail,
  PurchaseDocFile,
  PurchaseDocFileState,
  PurchaseDocSignature,
  PurchaseDocSigning,
} from "types/InvoiceInbox";
import { cancelFileSigning } from "services/files-category.service";
import { formatPrettyDate } from "utils/helperFunctions";
import { refusalMessage } from "./invoice-doc-files";
import InvoiceDocSignRequestDialog from "./invoice-doc-sign-request-dialog";
import { FileSignState, PRIMARY_FILE_KEY, fileSignState, isPendingSignatureOf, signaturesFor } from "./invoice-doc-signing";

/**
 * "Signatures": who was asked to sign which file of the document, who signed,
 * who declined — and the way to ask (Dokobit or Click) and to take a pending
 * request back.
 *
 * One block per file that has anything to show: the primary file first, then
 * the additional files in their own order. Cancelling goes through the
 * generic cancel-signing call — the server lets only the requester (the
 * file's owner) or a Master do it and says so otherwise.
 */

type Props = {
  companyId: string;
  doc: PurchaseDocDetail["doc"];
  files: PurchaseDocFile[];
  signatures: PurchaseDocSignature[];
  fileStates: Record<string, PurchaseDocFileState>;
  signing: PurchaseDocSigning | undefined;
  /** The logged-in user — a pending Click row of theirs gets a "Sign" link. */
  currentUserId: string | null;
  /** Reload the document (quietly) after a request was sent or cancelled. */
  onChanged: () => Promise<unknown> | void;
};

type Block = {
  key: string;
  name: string;
  state: FileSignState;
  signType: string | null;
  rows: PurchaseDocSignature[];
};

const STATE_BADGE: Record<Exclude<FileSignState, "none">, { key: string; defaultValue: string; className: string }> = {
  signed: { key: "invoice_inbox_file_signed", defaultValue: "Signed", className: "bg-green-100 text-green-800" },
  pending: {
    key: "invoice_inbox_sign_state_pending",
    defaultValue: "Signing in progress",
    className: "bg-amber-100 text-amber-800",
  },
  rejected: { key: "invoice_inbox_sign_state_rejected", defaultValue: "Declined", className: "bg-red-100 text-red-800" },
};

export default function InvoiceDocSignatures({
  companyId,
  doc,
  files,
  signatures,
  fileStates,
  signing,
  currentUserId,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => Boolean((i18n as any).exists && (i18n as any).exists(key));
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const deleted = Boolean(doc.deletedAt);

  const fallbackName = t("invoice_inbox_file_primary", { defaultValue: "Document" });
  const blocks: Block[] = [
    { key: PRIMARY_FILE_KEY, name: doc.fileName || fallbackName },
    ...files.map((file) => ({ key: file.id, name: file.fileName || fallbackName })),
  ]
    .map(({ key, name }) => ({
      key,
      name,
      state: fileSignState(key, signatures, fileStates),
      signType: (fileStates[key] && fileStates[key].signType) || null,
      rows: signaturesFor(signatures, key),
    }))
    .filter((block) => block.rows.length > 0 || block.state === "signed");

  const cancelRefusal = (err: any): string => {
    const data = err && err.response && err.response.data;
    const key = data && typeof data.message === "string" ? `invoice_inbox_cancel_signing_${data.message}` : "";
    return key && exists(key) ? t(key) : refusalMessage(t, exists, err);
  };

  const onCancel = async (block: Block) => {
    const pending = block.rows.find((row) => row.status === "pending");
    if (!pending) return;
    if (
      !window.confirm(
        t("invoice_inbox_sign_cancel_confirm", {
          defaultValue: "Cancel the signing of {{name}}? Those who have not signed yet will no longer be asked.",
          name: block.name,
        })
      )
    ) {
      return;
    }
    setCancelling(block.key);
    try {
      await cancelFileSigning(companyId, pending.fileId);
      toast.success(t("invoice_inbox_sign_cancelled", { defaultValue: "Signing cancelled" }));
      await onChanged();
    } catch (err) {
      toast.error(cancelRefusal(err));
    } finally {
      setCancelling(null);
    }
  };

  const signTypeLabel = (signType: string | null) =>
    signType ? t(`invoice_inbox_sign_type_${signType}`, { defaultValue: signType }) : null;

  const requestLabel = t("invoice_inbox_sign_request", { defaultValue: "Request signature" });

  return (
    <div className="space-y-3">
      {!blocks.length && (
        <p className="text-sm text-gray-500">
          {t("invoice_inbox_sign_none", { defaultValue: "No signatures have been requested." })}
        </p>
      )}

      {blocks.map((block) => {
        const badge = block.state === "none" ? null : STATE_BADGE[block.state];
        const mineToSign =
          block.signType === "Click" ? block.rows.find((row) => isPendingSignatureOf(row, currentUserId)) : undefined;
        return (
          <div key={block.key} data-testid={`invoice-doc-signatures-${block.key}`} className="rounded-md border p-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-0 truncate font-medium text-gray-900">{block.name}</span>
              {badge && (
                <span className={clsx("shrink-0 rounded px-1.5 py-0.5 text-xs font-medium", badge.className)}>
                  {t(badge.key, { defaultValue: badge.defaultValue })}
                </span>
              )}
              {block.signType && <span className="shrink-0 text-xs text-gray-500">{signTypeLabel(block.signType)}</span>}
            </div>

            {block.rows.length > 0 && (
              <ul className="mt-2 space-y-1">
                {block.rows.map((row) => {
                  const detail = row.position || row.email;
                  return (
                    <li key={row.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                      {row.status === "signed" ? (
                        <Check aria-hidden className="h-4 w-4 shrink-0 text-green-600" />
                      ) : row.status === "rejected" ? (
                        <X aria-hidden className="h-4 w-4 shrink-0 text-red-600" />
                      ) : (
                        <Clock aria-hidden className="h-4 w-4 shrink-0 text-amber-500" />
                      )}
                      <span className="text-gray-900">{row.name || row.email || "—"}</span>
                      {detail && detail !== row.name && <span className="text-gray-500">{detail}</span>}
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                        {row.ref === "Contacts"
                          ? t("invoice_inbox_sign_ref_contact", { defaultValue: "Supplier contact" })
                          : t("invoice_inbox_sign_ref_employee", { defaultValue: "Employee" })}
                      </span>
                      {row.status === "signed" && row.signingTime && (
                        <span className="text-xs text-gray-500">{formatPrettyDate(row.signingTime, "lt-LT", true)}</span>
                      )}
                      {row.status === "rejected" && row.declineReason && (
                        <span className="text-xs text-red-700">{row.declineReason}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {(block.state === "pending" || mineToSign) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {mineToSign && (
                  <Link
                    to={`/app/${companyId}/documents/view/${mineToSign.fileId}`}
                    className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
                  >
                    <PenLine aria-hidden className="h-4 w-4" />
                    {t("invoice_inbox_sign_now", { defaultValue: "Sign" })}
                  </Link>
                )}
                {block.state === "pending" && (
                  <button
                    type="button"
                    disabled={cancelling === block.key}
                    onClick={() => onCancel(block)}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t("invoice_inbox_sign_cancel", { defaultValue: "Cancel signing" })}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div>
        <button
          type="button"
          disabled={deleted}
          onClick={() => setRequesting(true)}
          title={
            deleted
              ? t("invoice_inbox_sign_request_deleted", {
                  defaultValue: "The document is deleted — restore it to ask for signatures.",
                })
              : requestLabel
          }
          className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PenLine aria-hidden className="h-4 w-4" />
          {requestLabel}
        </button>
      </div>

      {requesting && (
        <InvoiceDocSignRequestDialog
          companyId={companyId}
          doc={doc}
          files={files}
          signatures={signatures}
          fileStates={fileStates}
          signing={signing}
          onClose={() => setRequesting(false)}
          onSent={onChanged}
        />
      )}
    </div>
  );
}
